import React, { useEffect, useState } from "react";
import MenuItem from "@material-ui/core/MenuItem/MenuItem";
import CustomDropdown from "../Input/CustomDropdown";
import { HttpErrorType } from "../../models/HttpErrorType";
import DropdownSelectable from "./DropdownSelectable.model";

export interface CustomDynamicDropdownProps<T> {
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  labelText: string;
  mandatory?: boolean;
  value: T | null;
  load: () => Promise<T[]>;
  mapData: (data: T) => DropdownSelectable<T>;
  onGeneralError: (error: HttpErrorType) => void;
  loadKey?: string;
  onChange: (value: T | null) => void;
}

function CustomDynamicDropdown<T>(props: CustomDynamicDropdownProps<T>) {
  const { disabled, error, helperText, labelText, mandatory, load, loadKey, mapData, onGeneralError, onChange, value } =
    props;

  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState<DropdownSelectable<T>[]>([]);

  useEffect(() => {
    setLoading(true);
    load()
      .then((response) => response?.map(mapData))
      .then((newValues) => {
        if (value && !newValues.map((d) => d.key).includes(mapData(value).key)) {
          return [...newValues, mapData(value)];
        }
        return newValues;
      })
      .then(setValues)
      .catch(onGeneralError)
      .finally(() => setLoading(false));
  }, [loadKey]);

  useEffect(() => {
    setValues((prevState) => {
      if (value && !prevState.map((data) => data.key).includes(mapData(value).key)) {
        return [...prevState, mapData(value)];
      }
      return prevState;
    });
  }, [value]);

  if (loading) {
    return (
      <CustomDropdown helperText={helperText} labelText={labelText} mandatory={mandatory} error={error} value="">
        <MenuItem value="">
          <em>Loading...</em>
        </MenuItem>
      </CustomDropdown>
    );
  }

  return (
    <CustomDropdown
      disabled={disabled}
      helperText={helperText}
      labelText={labelText}
      mandatory={mandatory}
      error={error}
      value={value ? mapData(value).key : ""}
      onChange={(event) => {
        onChange(values.filter((v) => v.key === event.target.value).pop()?.data || null);
      }}
    >
      <MenuItem value="">
        <em>{values?.length === 0 ? "Nothing to select" : "Please Choose"}</em>
      </MenuItem>
      {values?.map((v) => (
        <MenuItem key={v.key} value={v.key}>
          {v.label}
        </MenuItem>
      ))}
    </CustomDropdown>
  );
}

export default CustomDynamicDropdown;
